//* Libraries imports
import { motion } from "framer-motion";
import { Card, Row, Col, Badge } from "react-bootstrap";

//* Icons imports
import {
  BsPersonFill,
  BsEnvelopeFill,
  BsTelephoneFill,
  BsGeoAltFill,
  BsCalendarEventFill,
  BsGenderAmbiguous,
  BsCardText,
  BsHouseFill,
} from "react-icons/bs";
import Icon from "../../../../components/common/Icon";

//* Styles imports
import "./enrollment-info.scss";

//* Types
import { Enrollment } from "../../../../schemas/common/enrollment/enrollment";

type EnrollmentInfoProps = {
  enrollment: Enrollment;
};

export function EnrollmentInfo({ enrollment }: EnrollmentInfoProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  const getSexoLabel = (sexo: "M" | "F" | "O") => {
    switch (sexo) {
      case "M":
        return "Masculino";
      case "F":
        return "Feminino";
      case "O":
        return "Outro";
      default:
        return sexo;
    }
  };

  const getNivelEnsinoLabel = (nivel: "fundamental" | "medio") => {
    return nivel === "fundamental" ? "Ensino Fundamental" : "Ensino Médio";
  };

  const getLinguaLabel = (lingua: "ingles" | "espanhol" | null) => {
    if (!lingua) return "Não informado";
    return lingua === "ingles" ? "Inglês" : "Espanhol";
  };

  const InfoItem = ({
    icon,
    label,
    value,
  }: {
    icon: any;
    label: string;
    value: string | null;
  }) => (
    <div className="info-item mb-3">
      <div className="d-flex align-items-center gap-2 mb-1">
        <Icon icon={icon} size={16} className="text-primary" />
        <span className="info-label text-muted small fw-semibold">{label}</span>
      </div>
      <div className="info-value ms-4">{value || "Não informado"}</div>
    </div>
  );

  return (
    <motion.div
      className="enrollment-info-wrapper"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      {/* Personal Information */}
      <Card className="info-card shadow-sm border-0 mb-4">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            <Icon icon={BsPersonFill} size={20} />
            Informações Pessoais
          </h5>
        </Card.Header>
        <Card.Body className="p-4">
          <Row>
            <Col md={6}>
              <InfoItem
                icon={BsPersonFill}
                label="Nome Completo"
                value={enrollment.nomeCompleto}
              />
              <InfoItem icon={BsCardText} label="CPF" value={enrollment.cpf} />
              <InfoItem
                icon={BsCalendarEventFill}
                label="Data de Nascimento"
                value={formatDate(enrollment.dataNascimento)}
              />
              <InfoItem
                icon={BsGenderAmbiguous}
                label="Sexo"
                value={getSexoLabel(enrollment.sexo)}
              />
            </Col>
            <Col md={6}>
              {enrollment.rg && (
                <InfoItem icon={BsCardText} label="RG" value={enrollment.rg} />
              )}
              {enrollment.orgaoEmissor && (
                <InfoItem
                  icon={BsCardText}
                  label="Órgão Emissor"
                  value={enrollment.orgaoEmissor}
                />
              )}
              {enrollment.dataExpedicaoRg && (
                <InfoItem
                  icon={BsCalendarEventFill}
                  label="Data de Expedição do RG"
                  value={formatDate(enrollment.dataExpedicaoRg)}
                />
              )}
              <InfoItem
                icon={BsPersonFill}
                label="Nome Completo da Mãe"
                value={enrollment.nomeCompletoMae}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="info-card shadow-sm border-0 mb-4">
        <Card.Header className="bg-success text-white">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            <Icon icon={BsEnvelopeFill} size={20} />
            Informações de Contato
          </h5>
        </Card.Header>
        <Card.Body className="p-4">
          <Row>
            <Col md={6}>
              <InfoItem
                icon={BsEnvelopeFill}
                label="E-mail"
                value={enrollment.email}
              />
            </Col>
            <Col md={6}>
              <InfoItem
                icon={BsTelephoneFill}
                label="Celular"
                value={enrollment.celular}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="info-card shadow-sm border-0 mb-4">
        <Card.Header className="bg-info text-white">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            <Icon icon={BsHouseFill} size={20} />
            Endereço
          </h5>
        </Card.Header>
        <Card.Body className="p-4">
          <Row>
            <Col md={6}>
              <InfoItem
                icon={BsGeoAltFill}
                label="CEP"
                value={enrollment.cep}
              />
              <InfoItem
                icon={BsHouseFill}
                label="Rua"
                value={enrollment.nomeRua}
              />
              <InfoItem
                icon={BsCardText}
                label="Número"
                value={enrollment.numero}
              />
              <InfoItem
                icon={BsCardText}
                label="Complemento"
                value={enrollment.complemento}
              />
            </Col>
            <Col md={6}>
              <InfoItem
                icon={BsGeoAltFill}
                label="Bairro"
                value={enrollment.bairro}
              />
              <InfoItem
                icon={BsGeoAltFill}
                label="Cidade"
                value={enrollment.cidade}
              />
              <InfoItem
                icon={BsGeoAltFill}
                label="Estado"
                value={enrollment.estado}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Academic Information */}
      <Card className="info-card shadow-sm border-0 mb-4">
        <Card.Header className="bg-warning text-dark">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            <Icon icon={BsCardText} size={20} />
            Informações Acadêmicas
          </h5>
        </Card.Header>
        <Card.Body className="p-4">
          <Row>
            <Col md={6}>
              <InfoItem
                icon={BsCardText}
                label="Nível de Ensino"
                value={getNivelEnsinoLabel(enrollment.nivelEnsino)}
              />
              <InfoItem
                icon={BsCardText}
                label="Língua Estrangeira"
                value={getLinguaLabel(enrollment.linguaEstrangeira)}
              />
            </Col>
            <Col md={6}>
              <div className="info-item mb-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Icon icon={BsCardText} size={16} className="text-primary" />
                  <span className="info-label text-muted small fw-semibold">
                    Áreas de Conhecimento
                  </span>
                </div>
                <div className="ms-4 d-flex flex-wrap gap-2">
                  {enrollment.areasConhecimento.length > 0 ? (
                    enrollment.areasConhecimento.map((area, index) => (
                      <Badge key={index} bg="secondary" className="px-3 py-2">
                        {area}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted">Nenhuma área selecionada</span>
                  )}
                </div>
              </div>
              {enrollment.condicoesEspeciais && (
                <InfoItem
                  icon={BsCardText}
                  label="Condições Especiais"
                  value={enrollment.condicoesEspeciais}
                />
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Process Information */}
      {(enrollment.faculdade || enrollment.processo) && (
        <Card className="info-card shadow-sm border-0 mb-4">
          <Card.Header className="bg-secondary text-white">
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <Icon icon={BsCardText} size={20} />
              Informações do Processo
            </h5>
          </Card.Header>
          <Card.Body className="p-4">
            <Row>
              {enrollment.processo && (
                <Col md={6}>
                  <InfoItem
                    icon={BsCardText}
                    label="Processo Seletivo"
                    value={enrollment.processo.name}
                  />
                </Col>
              )}
              {enrollment.faculdade && (
                <Col md={6}>
                  <InfoItem
                    icon={BsCardText}
                    label="Faculdade"
                    value={enrollment.faculdade.name}
                  />
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
      )}
    </motion.div>
  );
}
